/*------------------------------------------------------------------------------
   About      : AI Writing Assistant modal (epic DA-697, II-FE-6 / DA-2393)

                Mockup: `image-3.png` — tone, reading level, free-text
                instructions, and a Generate Alternative action.

                Backed by `POST /v1/insights/generate-alternative` (DA-2401),
                which calls OpenAI inside proxypulse. Two behaviours of that
                endpoint shape this UI:

                1. **Candidates are token-validated before they are returned**,
                   and validation FAILS CLOSED — if quill is unreachable a
                   candidate comes back `valid: false` with the reason rather
                   than unchecked. So this never treats an offered candidate as
                   safe; it renders `valid` per candidate.
                2. **Invalid candidates arrive flagged, not dropped.** That is
                   deliberate — the author sees *why* a suggestion was rejected
                   instead of an empty modal. So invalid ones are shown, marked,
                   and blocked from being applied.

                Guardrails are server-side (count clamped 1..3, templateString
                ≤2000, instructions ≤500); mirrored here so the user is stopped
                by a helper text rather than by a 400.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
         DialogContent, DialogTitle, MenuItem, Stack, TextField,
         Typography } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import { useGlobalConfig }  from 'context/GlobalContext'
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents'

import { InsightApis }        from '../api/insightAPI'
import { InsightAlternative } from '../types/insightTypes'

/** Server caps, mirrored so the user hits helper text rather than a 400. */
const MAX_INSTRUCTIONS = 500
const MAX_TEMPLATE     = 2000
const MAX_ALTERNATIVES = 3

const TONES = [
  'Neutral - Objective, data-driven',
  'Encouraging - Positive, supportive',
  'Urgent - Direct, action-oriented',
  'Friendly - Conversational, warm',
]

const READING_LEVELS = [
  '6th Grade - Simple',
  '8th Grade - Plain language',
  '12th Grade - Sophisticated',
]

interface AiWritingAssistantDialogProps {
  open              : boolean
  templateString    : string
  insightType      ?: string | null
  pilotId          ?: string
  insightDocumentId?: string
  onClose           : () => void
  /** Applies the chosen candidate to the template field. */
  onApply           : (text: string) => void
}

export const AiWritingAssistantDialog = ({
  open,
  templateString,
  insightType,
  pilotId,
  insightDocumentId,
  onClose,
  onApply,
}: AiWritingAssistantDialogProps) => {

  const { rc } = useGlobalConfig()

  const [tone, setTone]                 = useState(TONES[0])
  const [readingLevel, setReadingLevel] = useState(READING_LEVELS[2])
  const [instructions, setInstructions] = useState('')
  const [count, setCount]               = useState(1)

  const [isGenerating, setIsGenerating]   = useState(false)
  const [error, setError]                 = useState<string | undefined>()
  const [alternatives, setAlternatives]   = useState<InsightAlternative[]>([])
  const [model, setModel]                 = useState<string | undefined>()

  const tooLong          = templateString.length > MAX_TEMPLATE
  const instructionsLong = instructions.length > MAX_INSTRUCTIONS
  const canGenerate      = !!templateString.trim() && !tooLong && !instructionsLong && !isGenerating

  const generate = async () => {
    setIsGenerating(true)
    setError(undefined)
    setAlternatives([])

    const resp = await InsightApis.generateAlternative(rc, {
      templateString,
      tone,
      readingLevel,
      additionalInstructions : instructions || undefined,
      type                   : insightType,
      count,
      pilotId,
      insightDocumentId,
    })

    setIsGenerating(false)

    if (resp.error || !resp.data) {
      // proxypulse returns 503 when the model is unreachable and 502 when the
      // call fails — both arrive here with the server's own message, which is
      // more useful than a generic failure string.
      setError(resp.error?.message || 'Could not generate an alternative.')
      return
    }

    setAlternatives(resp.data.alternatives)
    setModel(resp.data.model)
  }

  const handleClose = () => {
    // Reset so reopening does not show a previous run's suggestions as if they
    // were generated for the current text.
    setAlternatives([])
    setError(undefined)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeIcon color="primary" />
          <Box>
            <Typography variant="h6">AI Writing Assistant</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the options below to generate a new version of your insight.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {!templateString.trim() && (
          <Alert severity="info" sx={{ mb : 2 }}>
            Write some template text first — the assistant rewrites what is there
            rather than composing from scratch.
          </Alert>
        )}

        {tooLong && (
          <Alert severity="error" sx={{ mb : 2 }}>
            Template text is {templateString.length} characters; the assistant accepts
            up to {MAX_TEMPLATE}.
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            select fullWidth size="small" label="Tone"
            value={tone} onChange={(e) => setTone(e.target.value)}
          >
            {TONES.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>

          <TextField
            select fullWidth size="small" label="Reading level"
            value={readingLevel} onChange={(e) => setReadingLevel(e.target.value)}
          >
            {READING_LEVELS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>

          <TextField
            fullWidth multiline minRows={2} size="small"
            label="Additional instructions (optional)"
            placeholder="e.g., Focus more on the appliance name…"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            error={instructionsLong}
            helperText={`${instructions.length} / ${MAX_INSTRUCTIONS}`}
          />

          <TextField
            select fullWidth size="small" label="Number of suggestions"
            value={count} onChange={(e) => setCount(Number(e.target.value))}
          >
            {Array.from({ length : MAX_ALTERNATIVES }, (_, i) => i + 1).map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </TextField>
        </Stack>

        {error && <Alert severity="error" sx={{ mt : 2 }}>{error}</Alert>}

        {isGenerating && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt : 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Generating…</Typography>
          </Stack>
        )}

        {!!alternatives.length && (
          <Box sx={{ mt : 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight : 600, mb : 1 }}>
              Suggestions{model ? ` · ${model}` : ''}
            </Typography>

            <Stack spacing={1.5}>
              {alternatives.map((alternative, index) => (
                <Box
                  key={index}
                  sx={{
                    p            : 1.5,
                    border       : '1px solid',
                    borderColor  : alternative.valid ? 'divider' : 'error.main',
                    borderRadius : 1,
                  }}
                >
                  <Typography variant="body2" sx={{ mb : 1 }}>{alternative.text}</Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={alternative.valid ? 'Tokens valid' : 'Invalid tokens'}
                      color={alternative.valid ? 'success' : 'error'}
                      variant="outlined"
                    />
                    <Box sx={{ flexGrow : 1 }} />
                    <Button
                      size="small"
                      variant="contained"
                      /* An invalid candidate is shown so the author understands
                         why it was rejected, but must not be applicable — its
                         tokens would ship literally to end users. */
                      disabled={!alternative.valid}
                      onClick={() => {
                        rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.AI_SUGGESTION_APPLIED)
                        onApply(alternative.text)
                        handleClose()
                      }}
                    >
                      Use this
                    </Button>
                  </Stack>

                  {!alternative.valid && !!alternative.validationErrors.length && (
                    <Alert severity="error" sx={{ mt : 1 }}>
                      {alternative.validationErrors.map((problem) => (
                        <Typography key={problem} variant="caption" sx={{ display : 'block' }}>
                          {problem}
                        </Typography>
                      ))}
                    </Alert>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          disabled={!canGenerate}
          onClick={() => {
            rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.AI_GENERATE_REQUESTED, {
              tone, reading_level : readingLevel, count,
            })
            generate()
          }}
        >
          {alternatives.length ? 'Regenerate' : 'Generate alternative'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AiWritingAssistantDialog
